import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Cake, Gift, Plus, Users } from "lucide-react";
import { CountUp } from "@/components/common/CountUp";
import { PageHeader } from "@/components/common/PageHeader";
import { PotFormDialog, KINDS } from "@/components/pots/PotFormDialog";
import { PotsSoon } from "@/components/soon/PotsSoon";
import { Button } from "@/components/ui/button";
import { useFeatures } from "@/hooks/use-features";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";

export const PotProgress = ({ collected, goal }) => {
  const pct = goal ? Math.min(100, Math.round((collected / goal) * 100)) : null;
  return pct === null ? null : <div className="h-2.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-success transition-[width] duration-1000" style={{ width: `${pct}%` }} /></div>;
};

const PotsList = () => {
  const { money, date } = useFormat();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["pots"], queryFn: () => api.get("/pots").then((r) => r.data) });
  if (!data) return <div className="h-48 animate-pulse rounded-2xl bg-muted" />;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="pot-create-open"><Plus className="mr-1 h-4 w-4" /> Créer une cagnotte</Button>
        <Button variant="outline" className="rounded-full" asChild><Link to="/proches" data-testid="pots-go-friends"><Users className="mr-1 h-4 w-4" /> Proches & amis</Link></Button>
      </div>
      {data.pots.length ? <ul className="stagger grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="pots-list">{data.pots.map((p) => (
        <li key={p.id}><Link to={`/cagnottes/${p.id}`} className="card-surface lift block space-y-2 p-5" data-testid={`pot-card-${p.id}`}>
          <div className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /><b className="flex-1 truncate">{p.title}</b><span className={p.open ? "badge-saving" : "rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-bold"}>{p.open ? "Ouverte" : "Clôturée"}</span></div>
          <div className="text-xs font-semibold text-muted-foreground">{KINDS[p.kind || "gift"]}{p.people ? ` · ${p.people} pers.` : ""}</div>
          <div className="num text-2xl font-extrabold"><CountUp value={p.collected} format={(x) => money(x, { maximumFractionDigits: 0 })} />{p.goal && <span className="text-sm font-medium text-muted-foreground"> / {money(p.goal, { maximumFractionDigits: 0 })}</span>}</div>
          <PotProgress collected={p.collected} goal={p.goal} />
          <div className="text-xs text-muted-foreground">{p.count} participation(s){p.end_date && ` · jusqu'au ${date(p.end_date)}`}</div>
        </Link></li>))}</ul> : <p className="text-sm text-muted-foreground">Aucune cagnotte pour l'instant.</p>}
      {data.contributions.length > 0 && <section className="space-y-2"><h2 className="text-lg">Mes participations</h2>
        <ul className="card-surface divide-y divide-border" data-testid="my-contributions">{data.contributions.map((c) => <li key={c.id} className="flex justify-between gap-3 p-3 text-sm"><span>{c.pot_title}</span><b className="num">{money(c.amount)}</b></li>)}</ul></section>}
      {open && <PotFormDialog onClose={() => setOpen(false)} />}
    </div>
  );
};

export const Birthdays = () => {
  const [pot, setPot] = useState(null);
  const { pots_enabled } = useFeatures();
  const { data } = useQuery({ queryKey: ["relatives"], queryFn: () => api.get("/relatives").then((r) => r.data) });
  const soon = (data?.relatives || []).filter((r) => r.days_until !== null && r.days_until <= 14).sort((a, b) => a.days_until - b.days_until);
  if (!soon.length) return null;
  return (
    <section className="decision-strong space-y-3 p-4" data-testid="birthday-reminders">
      <span className="badge-decision">Anniversaires à venir</span>
      <ul className="stagger grid gap-2 md:grid-cols-2">{soon.map((r) => {
        const ideas = r.ideas.filter((g) => g.status === "idea");
        const first = r.name.split(" ")[0];
        return (
          <li key={r.id} className="card-surface flex flex-col gap-2 p-3" data-testid={`birthday-${r.id}`}>
            <div className="flex items-center gap-2"><Cake className="h-5 w-5 text-warning" /><b className="flex-1">{r.name}</b><span className="num text-sm font-black">{r.days_until === 0 ? "Aujourd'hui !" : `J-${r.days_until}`}</span></div>
            <p className="text-xs text-muted-foreground">{ideas.length ? `Idées : ${ideas.map((g) => g.title).join(", ")}` : "Pas encore d'idée cadeau notée."}</p>
            {pots_enabled && <Button size="sm" className="self-start rounded-full" onClick={() => setPot(r)} data-testid={`birthday-pot-${r.id}`}><Gift className="mr-1 h-3.5 w-3.5" /> Lancer sa cagnotte</Button>}
          </li>
        );
      })}</ul>
      {pot && <PotFormDialog onClose={() => setPot(null)} preset={{ title: `Anniversaire de ${pot.name.split(" ")[0]}`, description: pot.ideas.filter((g) => g.status === "idea").map((g) => `• ${g.title}`).join("\n"), relative_id: pot.id }} />}
    </section>
  );
};

export default function Pots() {
  const [params] = useSearchParams();
  const { pots_enabled, loading } = useFeatures();
  if (params.get("tab") === "relatives") return <Navigate to={`/proches${params.get("proche") ? `?proche=${params.get("proche")}` : ""}`} replace />;
  if (loading) return <div className="h-64 animate-pulse rounded-2xl bg-muted" />;
  if (!pots_enabled) return <PotsSoon />;
  return (
    <div className="space-y-6" data-testid="pots-page">
      <PageHeader eyebrow="Cadeaux & projets" title="Cagnottes" text="Collectez pour un cadeau commun, un projet de vie ou un repas à partager." />
      <Birthdays />
      <PotsList />
    </div>
  );
}
